/**
 * tRPC Provider for Vite + React Router
 * Wraps the app with tRPC and React Query providers
 */
import { api, getTRPCClientConfig } from '@/utils/api';
import { captureException } from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

type TRPCProviderProps = {
    children: ReactNode;
};

export function TRPCProvider({ children }: TRPCProviderProps) {
    const [queryClient] = useState(() => {
        const handleApiError = (error: unknown): void => {
            captureException(error);
        };

        const queryCache = new QueryCache({
            onError: handleApiError,
        });
        const mutationCache = new MutationCache({
            onError: handleApiError,
        });

        return new QueryClient({
            queryCache,
            mutationCache,
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: false,
                },
            },
        });
    });

    const [trpcClient] = useState(() => api.createClient(getTRPCClientConfig()));

    return (
        <api.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </api.Provider>
    );
}
